/**
 * Fund Chart — Gráfico de rentabilidade com TradingView Lightweight Charts
 * Suporta dados manuais ou via API, múltiplas séries (fundo + benchmark),
 * botões de período e tooltip customizado.
 */

import { createChart, AreaSeries, LineSeries } from 'lightweight-charts';

export function initFundChart() {
	const containers = document.querySelectorAll('[data-fund-chart]');
	if ( ! containers.length ) return;

	containers.forEach( container => {
		const config = JSON.parse( container.getAttribute('data-fund-chart') || '{}' );
		const canvasEl = container.querySelector('[data-fund-chart-canvas]');
		if ( ! canvasEl ) return;

		// ── Meses abreviados pt-BR ──────────────────
		const mesesPt = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

		// ── Criar chart ─────────────────────────────
		const chart = createChart( canvasEl, {
			autoSize: true,
			layout: {
				background: { color: 'transparent' },
				textColor: '#909090',
				fontFamily: 'Inter, sans-serif',
				fontSize: 12,
			},
			grid: {
				vertLines: { visible: false },
				horzLines: { color: '#F0F0F0', style: 2 },
			},
			leftPriceScale: {
				visible: true,
				borderVisible: false,
				scaleMargins: { top: 0.1, bottom: 0.05 },
			},
			rightPriceScale: {
				visible: false,
			},
			timeScale: {
				borderVisible: false,
				timeVisible: false,
				fixLeftEdge: true,
				fixRightEdge: true,
				tickMarkFormatter: ( time, tickMarkType ) => {
					if ( tickMarkType >= 2 ) return '';
					const d = typeof time === 'string'
						? new Date( time + 'T00:00:00Z' )
						: new Date( time * 1000 );
					return mesesPt[ d.getUTCMonth() ] + ' ' + d.getUTCFullYear();
				},
			},
			localization: {
				priceFormatter: ( price ) => price.toLocaleString('en-US', { maximumFractionDigits: 0 }),
			},
			crosshair: {
				vertLine: {
					color: '#909090',
					width: 1,
					style: 3,
					labelBackgroundColor: '#404040',
				},
				horzLine: {
					color: '#909090',
					width: 1,
					style: 3,
					labelBackgroundColor: '#404040',
				},
			},
			handleScroll: { mouseWheel: false, vertTouchDrag: false },
			handleScale: { mouseWheel: false },
		});

		// ── Série principal (área com gradiente verde) ──
		const primarySeries = chart.addSeries( AreaSeries, {
			priceScaleId: 'left',
			lineColor: '#5ECFB8',
			lineWidth: 2,
			topColor: 'rgba(94, 207, 184, 0.4)',
			bottomColor: 'rgba(94, 207, 184, 0.0)',
			crosshairMarkerBackgroundColor: '#5ECFB8',
			crosshairMarkerBorderColor: '#fff',
			crosshairMarkerRadius: 5,
			priceLineVisible: false,
		});

		// ── Série benchmark (linha vermelha/marrom) ─────
		const benchmarkSeries = chart.addSeries( LineSeries, {
			priceScaleId: 'left',
			color: '#C4776E',
			lineWidth: 1.5,
			crosshairMarkerVisible: false,
			priceLineVisible: false,
			lastValueVisible: false,
		});

		// ── Tooltip customizado ─────────────────────────
		const tooltip = document.createElement('div');
		tooltip.className = 'fund-chart__tooltip';
		canvasEl.appendChild( tooltip );

		chart.subscribeCrosshairMove( param => {
			if ( ! param.time || ! param.point || param.point.x < 0 || param.point.y < 0 ) {
				tooltip.classList.remove('is-visible');
				return;
			}

			const entry = param.seriesData.get( primarySeries );
			if ( ! entry ) {
				tooltip.classList.remove('is-visible');
				return;
			}

			const t = param.time;
			const d = typeof t === 'string'
				? new Date( t + 'T00:00:00Z' )
				: new Date( t * 1000 );

			const dateStr = mesesPt[ d.getUTCMonth() ] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
			const value   = typeof entry === 'object' ? entry.value : entry;
			const valStr  = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

			tooltip.innerHTML =
				'<span class="fund-chart__tooltip-date">' + dateStr + '</span>' +
				'<span class="fund-chart__tooltip-value">' + valStr + '</span>';

			const chartWidth   = canvasEl.clientWidth;
			const tooltipWidth = tooltip.offsetWidth || 160;
			let   left         = param.point.x - tooltipWidth / 2;
			left = Math.max( 0, Math.min( left, chartWidth - tooltipWidth ) );

			const tooltipHeight = tooltip.offsetHeight || 72;
			let   top           = param.point.y - tooltipHeight - 16;
			if ( top < 0 ) top = param.point.y + 20;

			tooltip.style.left = left + 'px';
			tooltip.style.top  = top  + 'px';
			tooltip.classList.add('is-visible');
		});

		// ── Carregar dados ──────────────────────────────
		function loadData( period ) {
			if ( config.apiUrl ) {
				// Fetch da API
				const url = config.apiUrl + ( config.apiUrl.includes('?') ? '&' : '?' ) + 'period=' + period;
				fetch( url )
					.then( res => res.json() )
					.then( data => {
						if ( data.primary ) primarySeries.setData( data.primary );
						if ( data.benchmark ) benchmarkSeries.setData( data.benchmark );
						chart.timeScale().fitContent();
					})
					.catch( err => console.warn('Fund chart API error:', err) );
			} else if ( config.series && ( config.series.primary || config.series.benchmark ) ) {
				// Dados manuais
				if ( config.series.primary ) primarySeries.setData( config.series.primary );
				if ( config.series.benchmark ) benchmarkSeries.setData( config.series.benchmark );
				chart.timeScale().fitContent();
			} else {
				// Dados de demonstração
				const demoData = generateDemoData( period );
				primarySeries.setData( demoData.primary );
				benchmarkSeries.setData( demoData.benchmark );
				chart.timeScale().fitContent();
			}
		}

		// ── Botões de período ───────────────────────────
		const periodBtns = container.querySelectorAll('[data-periodo]');
		periodBtns.forEach( btn => {
			btn.addEventListener('click', () => {
				periodBtns.forEach( b => b.classList.remove('is-active') );
				btn.classList.add('is-active');
				loadData( btn.getAttribute('data-periodo') );
			});
		});

		// ── Init ────────────────────────────────────────
		loadData( config.periodoAtivo || '6m' );

		// ── Drag hint (mobile) ───────────────────────────
		const dragHint = container.querySelector('[data-fund-chart-drag]');
		if ( dragHint ) {
			const canvasWrapper = container.querySelector('.fund-chart__canvas-wrapper');
			if ( canvasWrapper ) {
				canvasWrapper.addEventListener( 'touchstart', () => {
					dragHint.classList.add('is-hidden');
				}, { passive: true, once: true } );
			}
		}

		// ── Resize observer ─────────────────────────────
		const resizeObserver = new ResizeObserver( () => {
			chart.applyOptions({ autoSize: true });
		});
		resizeObserver.observe( canvasEl );

	});
}

/**
 * Gera dados de demonstração para preview
 */
function generateDemoData( period ) {
	const now = new Date();
	let days = 180; // default 6m

	switch( period ) {
		case '1d':  days = 1; break;
		case '5d':  days = 5; break;
		case '1m':  days = 30; break;
		case '6m':  days = 180; break;
		case '1y':  days = 365; break;
		case '5y':  days = 1825; break;
		case 'Max': days = 2555; break;
	}

	const primary = [];
	const benchmark = [];
	let pValue = 4100;
	let bValue = 4050;

	for ( let i = days; i >= 0; i-- ) {
		const date = new Date( now );
		date.setDate( date.getDate() - i );
		const time = date.toISOString().split('T')[0];

		pValue += ( Math.random() - 0.45 ) * 30;
		bValue += ( Math.random() - 0.48 ) * 20;

		primary.push({ time, value: Math.round( pValue * 100 ) / 100 });
		benchmark.push({ time, value: Math.round( bValue * 100 ) / 100 });
	}

	return { primary, benchmark };
}
